/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { CreditCardOutlined, LoadingOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import {
  isSportsPayCompleteMessage,
  readSportsPayCompletionFields,
  resolveSportsPayAppLink,
  resolveSportsPaySignupButtonState,
} from '@sportninja/common/features/account/sportsPaySignUp'
import type {
  SportsPayEligibleOrganization,
  SportsPayViewerPermission,
} from '@sportninja/common/features/account/sportsPaySignUp'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { App } from 'antd'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { paragraphSmall, weightSemiBold } from 'src/components/css'
import { useNotifySportsPayComplete } from '../hooks/useNotifySportsPayComplete'
import { useSportsPayUrl } from '../hooks/useSportsPayUrl'
import SportsPayAppLinkModal from './SportsPayAppLinkModal'

type SportsPayUrlButtonProps = {
  /** Organization the registration is for. */
  organizationId?: string
  /**
   * The organization payload. Only the eligibility gate reads it —
   * `is_affiliate` plus `sportspay.is_onboarded` decide whether the button is
   * offered at all.
   */
  organization?: SportsPayEligibleOrganization
  /**
   * The viewer's permissions on this organization. Only an admin may start a
   * payment-provider signup.
   */
  permission?: SportsPayViewerPermission
  /**
   * Re-read the organization, so a completed registration flips the button off.
   *
   * Called when the embedded frame closes and when the tab regains focus —
   * `is_onboarded` changes on SportsPay's side, so those are the only two
   * moments we can know something might have happened.
   */
  onOnboardingChanged?: () => void
}

/**
 * Entry point to the SportsPay HOSTED registration flow.
 *
 * Clicking fetches a one-time registration URL
 * (`GET …/payment-provider/sportspay/url`) and opens it in the embedded frame.
 * The server builds the whole URL — callback, org id, contact and address
 * prefill — so nothing is collected here.
 *
 * This REPLACES `SportsPaySignupButton`, which drove the in-app application
 * form and its POST. That flow is intact and still tested; it is simply not
 * rendered anywhere. Restoring it is a matter of swapping which button
 * `PageHeader` renders.
 */
const SportsPayUrlButton: React.FC<SportsPayUrlButtonProps> = ({
  organizationId,
  organization,
  permission,
  onOnboardingChanged,
}) => {
  'use memo'

  const { message } = App.useApp()
  const [appLink, setAppLink] = useState<string | null>(null)
  const urlRequest = useSportsPayUrl(organizationId)
  const { mutateAsync, isPending } = urlRequest
  const notifyComplete = useNotifySportsPayComplete()
  /** True while the completion PUT is in flight, so the frame can say so. */
  const [isFinalizing, setIsFinalizing] = useState(false)

  /**
   * Whether the frame has been opened at least once this session. Gates the
   * focus refetch, so merely tabbing away from a page whose button was never
   * pressed does not trigger a read.
   */
  const hasOpenedRef = useRef(false)

  const handleOpen = useCallback(async () => {
    try {
      const url = await mutateAsync()
      // Same https-only check the rest of the feature uses: this lands in an
      // iframe `src`, where a `javascript:` URL would execute.
      const safeUrl = resolveSportsPayAppLink(url)
      if (!safeUrl) {
        message.error('SportsPay did not return a usable registration link.')
        return
      }
      hasOpenedRef.current = true
      setAppLink(safeUrl)
    } catch (err) {
      message.error(getErrorMessage(err))
    }
  }, [mutateAsync, message])

  /**
   * Closing the frame is the first moment the registration may have advanced.
   *
   * The refetch is DEFERRED to a macrotask on purpose. Running it inline
   * unmounted this modal and re-rendered the whole org page inside the close
   * button's own click dispatch — by the time the browser delivered `click`,
   * the button was gone and the element revealed underneath it (the toolbar's
   * plugin icon) received it instead, opening the plugin modal.
   *
   * Letting the click finish first means the modal is already gone before the
   * org re-render begins, so there is nothing to click through to.
   */
  const handleCloseFrame = useCallback(() => {
    setAppLink(null)
    if (!onOnboardingChanged) {
      return
    }
    const refetch = onOnboardingChanged
    setTimeout(() => refetch(), 0)
  }, [onOnboardingChanged])

  /**
   * The registration announced itself as complete.
   *
   * SportsPay's callback loads OUR org page inside the frame, and that framed
   * copy posts up to us before it renders (see `announceSportsPayCallback`).
   * This is the precise signal — the close and visibility refetches below are
   * the fallbacks for when the user leaves another way.
   */
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (!isSportsPayCompleteMessage(event, window.location.origin)) {
        return
      }

      const params = (event.data as { params?: Record<string, string> })?.params
      const fields = readSportsPayCompletionFields(params)
      const accountId = organization?.account_id

      // The frame stays up behind the "finalizing" overlay until the PUT
      // settles, so the user is never left wondering what happened.
      setIsFinalizing(true)
      try {
        if (fields && accountId) {
          await notifyComplete.mutateAsync({ accountId, ...fields })
        }
        message.success('Your SportsPay registration is complete.')
      } catch (err) {
        // Surface it, but do NOT trap the user in the frame: the refetch below
        // still runs, so if the backend recorded completion another way the
        // button updates anyway.
        message.error(getErrorMessage(err))
      } finally {
        setIsFinalizing(false)
        setAppLink(null)
        onOnboardingChanged?.()
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [message, onOnboardingChanged, notifyComplete, organization?.account_id])

  /**
   * Returning to this TAB is the other moment — the registration can be
   * completed in a new tab via the frame's "Open in New Tab" link, and coming
   * back is the only signal we get that it might have finished.
   *
   * `visibilitychange`, NOT a window `focus` listener. Window focus also fires
   * when focus moves OUT OF THE IFRAME back to the parent document, which
   * happens on the very click that closes the frame — so it refetched the whole
   * org page mid-click, tearing the modal out from under the pointer and
   * letting the press land on the toolbar button revealed underneath.
   * `visibilitychange` only fires for real tab/window switches.
   */
  useEffect(() => {
    const handleVisible = () => {
      if (document.visibilityState === 'visible' && hasOpenedRef.current) {
        onOnboardingChanged?.()
      }
    }
    document.addEventListener('visibilitychange', handleVisible)
    return () => document.removeEventListener('visibilitychange', handleVisible)
  }, [onOnboardingChanged])

  const buttonState = resolveSportsPaySignupButtonState(
    organization,
    permission
  )
  if (!organizationId || buttonState === 'hidden') {
    return null
  }

  /*
   * SportsPay has issued a terminal but the account is not live yet. The button
   * stays visible so the org can see something is underway, but starting a
   * second registration would be wrong — hence disabled rather than hidden.
   */
  const isInProgress = buttonState === 'disabled'
  const isBusy = isPending || isInProgress

  return (
    <>
      <button
        type='button'
        onClick={handleOpen}
        disabled={isBusy}
        css={buttonStyle(isBusy)}
        title={
          isInProgress
            ? 'SportsPay is still setting up this account.'
            : undefined
        }
      >
        {isPending ? (
          <LoadingOutlined css={iconStyle} spin />
        ) : (
          <CreditCardOutlined css={iconStyle} />
        )}
        {capitalizeWords(
          isInProgress
            ? 'Sportspay Registration In Progress'
            : isPending
            ? 'Opening…'
            : 'Sign Up To Accept Credit Card Registrations'
        )}
      </button>

      <SportsPayAppLinkModal
        isOpen={Boolean(appLink)}
        onClose={handleCloseFrame}
        appLink={appLink ?? ''}
        isFinalizing={isFinalizing}
      />
    </>
  )
}

export default SportsPayUrlButton

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const iconStyle = css`
  font-size: 16px;
  color: ${PRIMARY_BUTTON_CONTENT.icon};
`

const buttonStyle = (isDisabled: boolean) => css`
  ${paragraphSmall}
  ${weightSemiBold}
  letter-spacing: 0.6px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  white-space: nowrap;
  cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
  background: ${colors.PRIMARY};
  color: ${PRIMARY_BUTTON_CONTENT.text};
  border: 1px solid ${colors.PRIMARY};
  padding: 8px;
  border-radius: 8px;
  opacity: ${isDisabled ? 0.6 : 1};
  transition: opacity 0.2s;
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`
